import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, Eye, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { QuestionBuilder } from './QuestionBuilder';
import { exportTemplateToWord } from './WordExportUtils';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '@/styles/quill-template.css';

interface SurveyTemplate {
  id: string;
  name: string;
  description: string;
  template_type: string;
  questions: any[];
  kpi_mappings: Record<string, string>;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

interface SurveyTemplateManagerProps {
  onSelectTemplate?: (template: SurveyTemplate) => void;
}

export function SurveyTemplateManager({ onSelectTemplate }: SurveyTemplateManagerProps) {
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<SurveyTemplate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_type: 'course_evaluation',
    questions: [],
    kpi_mappings: {},
    is_active: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      // Using mock data until types are updated - but with more realistic behavior
      const mockTemplates: SurveyTemplate[] = [
        {
          id: '1',
          name: 'Course Evaluation Standard',
          description: '<p>Standard course evaluation template with <strong>comprehensive feedback questions</strong> designed to gather insights on:</p><ul><li>Overall course effectiveness</li><li>Content quality and relevance</li><li>Instructor performance</li><li>Learning outcomes achievement</li></ul>',
          template_type: 'course_evaluation',
          questions: [
            {
              id: 'overall_rating',
              type: 'rating',
              question: 'Overall, how would you rate this course?',
              required: true,
              options: { scale: 5, labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] }
            },
            {
              id: 'content_quality',
              type: 'rating',
              question: 'How would you rate the quality of the content?',
              required: true,
              options: { scale: 5 }
            }
          ],
          kpi_mappings: {},
          is_active: true,
          is_default: true,
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          name: 'Classroom Walkthrough',
          description: '<p>Template for <em>classroom observation</em> and walkthrough evaluations.</p><p>This comprehensive template includes sections for:</p><ul><li>Lesson objective clarity</li><li>Student engagement levels</li><li>Instructional strategies</li><li>Areas of strength and improvement</li></ul>',
          template_type: 'walkthrough',
          questions: [
            {
              id: 'lesson_objective',
              type: 'text',
              question: 'Was the lesson objective clearly stated?',
              required: true
            },
            {
              id: 'student_engagement',
              type: 'rating',
              question: 'Rate the level of student engagement',
              required: true,
              options: { scale: 5 }
            }
          ],
          kpi_mappings: {},
          is_active: true,
          is_default: false,
          created_at: new Date(Date.now() - 86400000).toISOString()
        }
      ];
      
      setTemplates([...mockTemplates, ...templates.filter(t => !t.is_default)]);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error loading templates",
        description: "Failed to load survey templates",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        toast({
          title: "Validation Error",
          description: "Template name is required",
          variant: "destructive"
        });
        return;
      }

      // Create new template with generated questions
      const newTemplate: SurveyTemplate = {
        id: editingTemplate?.id || Date.now().toString(),
        name: formData.name,
        description: formData.description,
        template_type: formData.template_type,
        questions: generateDefaultQuestions(formData.template_type),
        kpi_mappings: formData.kpi_mappings,
        is_active: formData.is_active,
        is_default: false,
        created_at: editingTemplate?.created_at || new Date().toISOString()
      };

      if (editingTemplate) {
        // Update existing template
        setTemplates(templates.map(t => t.id === editingTemplate.id ? newTemplate : t));
        toast({ 
          title: "Template Updated!",
          description: `"${formData.name}" has been updated successfully`,
        });
      } else {
        // Add new template
        setTemplates([newTemplate, ...templates]);
        toast({ 
          title: "Template Created!",
          description: `"${formData.name}" template has been created successfully`,
        });
      }

      setIsDialogOpen(false);
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Error saving template",
        description: "Failed to save survey template. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Generate default questions based on template type
  const generateDefaultQuestions = (type: string) => {
    switch (type) {
      case 'course_evaluation':
        return [
          {
            id: 'overall_rating',
            type: 'rating',
            question: 'Overall, how would you rate this course?',
            required: true,
            options: { scale: 5, labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] }
          },
          {
            id: 'content_quality',
            type: 'rating',
            question: 'How would you rate the quality of the content?',
            required: true,
            options: { scale: 5 }
          },
          {
            id: 'recommend',
            type: 'yes_no',
            question: 'Would you recommend this course to a colleague?',
            required: true
          },
          {
            id: 'feedback',
            type: 'text',
            question: 'Any additional feedback?',
            required: false,
            options: { multiline: true }
          }
        ];
      case 'walkthrough':
        return [
          {
            id: 'lesson_objective',
            type: 'text',
            question: 'Was the lesson objective clearly stated?',
            required: true
          },
          {
            id: 'student_engagement',
            type: 'rating',
            question: 'Rate the level of student engagement',
            required: true,
            options: { scale: 5 }
          },
          {
            id: 'instructional_strategies',
            type: 'text',
            question: 'Describe the instructional strategies observed',
            required: true,
            options: { multiline: true }
          },
          {
            id: 'areas_of_strength',
            type: 'text',
            question: 'Areas of strength observed',
            required: false,
            options: { multiline: true }
          },
          {
            id: 'suggestions',
            type: 'text',
            question: 'Suggestions for improvement',
            required: false,
            options: { multiline: true }
          }
        ];
      case 'performance_evaluation':
        return [
          {
            id: 'job_performance',
            type: 'rating',
            question: 'Overall job performance rating',
            required: true,
            options: { scale: 5 }
          },
          {
            id: 'goals_met',
            type: 'yes_no',
            question: 'Were performance goals met?',
            required: true
          },
          {
            id: 'strengths',
            type: 'text',
            question: 'Key strengths',
            required: true,
            options: { multiline: true }
          },
          {
            id: 'development_areas',
            type: 'text',
            question: 'Areas for development',
            required: false,
            options: { multiline: true }
          }
        ];
      default:
        return [
          {
            id: 'general_feedback',
            type: 'text',
            question: 'Please provide your feedback',
            required: true,
            options: { multiline: true }
          }
        ];
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      // Remove from state
      setTemplates(templates.filter(t => t.id !== id));
      toast({ 
        title: "Template Deleted",
        description: "Survey template has been deleted successfully" 
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error deleting template",
        description: "Failed to delete survey template. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDuplicateTemplate = async (template: SurveyTemplate) => {
    try {
      const duplicateData = {
        name: `${template.name} (Copy)`,
        description: template.description,
        template_type: template.template_type,
        questions: template.questions,
        kpi_mappings: template.kpi_mappings,
        is_active: false
      };

      // Create new template in state
      const newTemplate: SurveyTemplate = {
        ...duplicateData,
        id: Date.now().toString(),
        is_default: false,
        created_at: new Date().toISOString()
      };

      setTemplates([newTemplate, ...templates]);
      toast({ title: "Template duplicated successfully" });
    } catch (error) {
      toast({
        title: "Error duplicating template",
        description: "Failed to duplicate survey template",
        variant: "destructive"
      });
    }
  };

  const handleExportToWord = async (template: SurveyTemplate) => {
    try {
      setIsExporting(template.id);
      await exportTemplateToWord(template);
      toast({
        title: "Export Successful!",
        description: `"${template.name}" has been downloaded as a Word document`,
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(null);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link'
  ];

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template_type: 'course_evaluation',
      questions: [],
      kpi_mappings: {},
      is_active: true
    });
  };

  const openEditDialog = (template?: SurveyTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        template_type: template.template_type,
        questions: template.questions,
        kpi_mappings: template.kpi_mappings || {},
        is_active: template.is_active
      });
    } else {
      setEditingTemplate(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'course_evaluation': return 'bg-blue-100 text-blue-800';
      case 'performance_evaluation': return 'bg-green-100 text-green-800';
      case 'walkthrough': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Survey Templates</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openEditDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {editingTemplate 
                      ? 'Modify the existing survey template details' 
                      : 'Create a new reusable survey template with predefined questions'
                    }
                  </p>
                </div>
                <DialogClose asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <X className="h-4 w-4" />
                  </Button>
                </DialogClose>
              </div>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Template Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Template Type</Label>
                  <Select
                    value={formData.template_type}
                    onValueChange={(value) => setFormData({ ...formData, template_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="course_evaluation">Course Evaluation</SelectItem>
                      <SelectItem value="performance_evaluation">Performance Evaluation</SelectItem>
                      <SelectItem value="walkthrough">Walkthrough</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <div className="mt-1">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter a detailed description of this survey template..."
                    style={{ minHeight: '120px' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the formatting tools to create professional template descriptions
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active Template</Label>
              </div>

              <div>
                <Label>Questions</Label>
                <div className="text-sm text-muted-foreground">
                  Questions will be automatically generated based on the template type selected above.
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveTemplate}>
                  {editingTemplate ? 'Update' : 'Create'} Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {templates.map((template) => (
          <Card key={template.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {template.name}
                    {template.is_default && (
                      <Badge variant="secondary">Default</Badge>
                    )}
                    {!template.is_active && (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                  </CardTitle>
                  <div 
                    className="text-sm text-muted-foreground mt-1"
                    dangerouslySetInnerHTML={{ __html: template.description }}
                  />
                </div>
                <Badge className={getTypeColor(template.template_type)}>
                  {template.template_type.replace('_', ' ')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {template.questions.length} questions • 
                  Created {new Date(template.created_at).toLocaleDateString()}
                </div>
                <div className="flex space-x-2">
                  {onSelectTemplate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelectTemplate(template)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Use
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportToWord(template)}
                    disabled={isExporting === template.id}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {isExporting === template.id ? 'Exporting...' : 'Download'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDuplicateTemplate(template)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(template)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {!template.is_default && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No survey templates found.</p>
            <Button
              className="mt-4"
              onClick={() => openEditDialog()}
            >
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}